import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { useIntl, useFeatureToggles } from "@grandvisionhq/state";
import { Util } from "@grandvisionhq/stores-v2";
import { useTrackPageView } from "@grandvisionhq/svc-tealium/events";
import { useScrollIntoView, useDebouncedEffect, DateUtils } from "@grandvisionhq/utils";
import { useTimeFilterContext } from "../../../../../context/TimeFilterContext/v3/context/TimeFilterContext/TimeFilterContext.js";
import { appointmentBookingAlternativeLocationEvent, bookAppointmentWhenStepScreenEvent } from "../../../../../data-layer/v3/data-layer/actions.js";
import { usePreferredTimeDataLayerEventDispatchers } from "../../../../../data-layer/hooks/v3/data-layer/hooks/use-preferred-time-dl-event-dispatchers.js";
import { useActiveTimeFilters } from "../../../../../hooks/v3/hooks/use-active-time-filters.js";
import { useAppointmentDurationLabel } from "../../../../../hooks/v3/hooks/use-appointment-duration-label.js";
import { useAppointmentContext } from "../../../../../pages/BookAppointment/context/v3/pages/BookAppointment/context/AppointmentContext.js";
import { useRoutingContext } from "../../../../../pages/BookAppointment/context/RoutingContext/v3/pages/BookAppointment/context/RoutingContext/context.js";
import useBookAppointmentStoreById from "../../../../../pages/BookAppointment/hooks/api/v3/pages/BookAppointment/hooks/api/use-book-appointment-store-by-id.js";
import useBookAppointmentType from "../../../../../pages/BookAppointment/hooks/api/v3/pages/BookAppointment/hooks/api/use-book-appointment-type.js";
import useBookAppointmentStoreWithUrlParam from "../../../../../pages/BookAppointment/hooks/v3/pages/BookAppointment/hooks/use-book-appointment-store-with-url-param.js";
import useBookAppointmentTypeWithUrlParam from "../../../../../pages/BookAppointment/hooks/v3/pages/BookAppointment/hooks/use-book-appointment-type-with-url-param.js";
import useVirtualAppointment from "../../../../../pages/BookAppointment/hooks/v3/pages/BookAppointment/hooks/use-virtual-appointment.js";
import { getWeekDaysForWeeksFromDate } from "../../../../../utils/v3/utils/datetime.js";
import { getClosedDates, firstAvailableDate, getCalendarTimeSlotsAvailability } from "../../../../AvailabilityCalendar/v3/components/AvailabilityCalendar/utils.js";
import { Variant } from "../../../../NoTimeSlots/v3/components/NoTimeSlots/types.js";
import AppointmentDateTime from "./AppointmentDateTime.js";
import useShouldRequestAlternativeStoreSuggestions from "./useShouldRequestAlternativeStoreSuggestions.js";
import useCalendarAvailabilityById from "../../../../../hooks/api/v3/hooks/api/use-calendar-availability-by-id.js";
import { useStoreOpeningTimesById } from "../../../../../hooks/api/v3/hooks/api/use-store-opening-times-by-id.js";
import useTimeSlotsById from "../../../../../hooks/api/v3/hooks/api/use-time-slots-by-id.js";
import { useNextAvailableDate } from "../../../../NoTimeSlots/hooks/v3/components/NoTimeSlots/hooks/use-next-available-date.js";
import { useNextWeekDate } from "../../../../NoTimeSlots/hooks/v3/components/NoTimeSlots/hooks/use-next-week-date.js";
import { useNoTimeSlotsVariant } from "../../../../NoTimeSlots/hooks/v3/components/NoTimeSlots/hooks/use-no-timeslot-variant.js";
import { isPreferredTimeFilterActive } from "../../../../../utils/v3/utils/is-preferred-time-filter-active.js";
const AppointmentDateTimeContainer = ({
  shouldRequestAvailabilityOnMount = true,
  ...rest
}) => {
  const { getLabel, getFormattedLabel, locale } = useIntl();
  const [{ setStore, setTimeSlot }, { store, type, timeSlot }] = useAppointmentContext();
  const [{ goTo }, { queryParams }] = useRoutingContext();
  const [{ isVirtualAppointmentType }] = useVirtualAppointment();
  const isVirtualAppointment = isVirtualAppointmentType(type);
  const { elementRef: otherTimeSlotsElement, triggerScroll } = useScrollIntoView({
    block: "center"
  });
  const [, { active }] = useTimeFilterContext();
  const [{ getAvailabilityTimeFilters }, { activeFilters: activeTimeFilters }] = useActiveTimeFilters();
  const today = React.useRef(/* @__PURE__ */ new Date());
  const [weekDate, setWeekDate] = React.useState(
    () => (
      // first day of first week
      getWeekDaysForWeeksFromDate(today.current)?.[0]?.[0]
    )
  );
  const [weeks, setWeeks] = React.useState();
  const [date, setDate] = React.useState();
  const [availabilityRequestParamsChanged, setAvailabilityRequestParamsChanged] = React.useState(
    shouldRequestAvailabilityOnMount
  );
  const [showOtherTimeSlots, setShowOtherTimeSlots] = React.useState(false);
  const { isMultilingualEnabled, featureConfigs, isAabAlternativeStoreAvailabilityEnabled } = useFeatureToggles();
  const {
    appointmentBookingUrlPaddedLength,
    appointmentBookingAlternativeUrl,
    appointmentBookingAlternativeUrlStores
  } = featureConfigs;
  const [getCalendarAvailability, calendarAvailability] = useCalendarAvailabilityById();
  const [, bookAppointmentType] = useBookAppointmentType();
  const [, bookAppointmentStore] = useBookAppointmentStoreById();
  const [getStoreOpeningTimes, storeOpeningTimes] = useStoreOpeningTimesById();
  useBookAppointmentStoreWithUrlParam();
  useBookAppointmentTypeWithUrlParam();
  const appointmentBookingAvailabiltyCalendarWeekCount = featureConfigs?.appointmentBookingAvailabiltyCalendarWeekCount;
  const durationLabel = useAppointmentDurationLabel(type);
  const labels = React.useMemo(() => {
    return {
      title: getLabel("appointments.component.appointmentDateTimeContainer.title"),
      selectATime: getLabel("appointments.component.appointmentDateTimeContainer.selectATime"),
      selectATimeSubtitle: durationLabel ? getFormattedLabel(
        "appointments.component.appointmentDateTimeContainer.selectATimeSubtitle",
        {
          labelData: {
            duration: durationLabel
          }
        }
      ) : "",
      showOtherTimeSlots: getLabel(
        "appointments.component.appointmentDateTimeContainer.showOtherTimeSlots"
      ),
      hideOtherTimeSlots: getLabel(
        "appointments.component.appointmentDateTimeContainer.hideOtherTimeSlots"
      ),
      otherTimeSlots: getLabel(
        "appointments.component.appointmentDateTimeContainer.otherTimeSlots"
      )
    };
  }, [durationLabel, getLabel, getFormattedLabel]);
  const closedDates = React.useMemo(() => {
    if (!weeks || !storeOpeningTimes.data) {
      return [];
    }
    return getClosedDates(weeks, storeOpeningTimes.data);
  }, [weeks, storeOpeningTimes.data]);
  const requestCalendarAvailability = React.useCallback(() => {
    const appointmentTypeId = type?.id;
    const globalStoreId = store?.globalStoreId;
    if (!appointmentTypeId || !globalStoreId || !weeks) {
      if (process.env.NODE_ENV === "test") {
        console.log("Awaiting availability request variables data");
      }
      return;
    }
    const firstWeek = weeks[0];
    const firstWeekDay = firstWeek?.[0];
    const lastWeek = weeks[weeks.length - 1];
    const lastWeekDay = lastWeek?.[lastWeek.length - 1];
    if (firstWeekDay && lastWeekDay) {
      getCalendarAvailability({
        appointmentTypeId,
        globalStoreId,
        startDate: firstWeekDay,
        endDate: lastWeekDay,
        ...getAvailabilityTimeFilters()
      });
    } else {
      console.error("Missing calendar data: startDate or endDate is undefined");
    }
    setAvailabilityRequestParamsChanged(false);
  }, [getAvailabilityTimeFilters, getCalendarAvailability, store?.globalStoreId, type?.id, weeks]);
  useDebouncedEffect(
    () => {
      if (!calendarAvailability) {
        requestCalendarAvailability();
      }
    },
    400,
    [requestCalendarAvailability]
  );
  React.useEffect(() => {
    if (calendarAvailability) {
      requestCalendarAvailability();
    }
  }, [requestCalendarAvailability]);
  const onCalendarWeeksChange = React.useCallback((weeks2) => {
    setAvailabilityRequestParamsChanged(true);
    setWeeks(weeks2);
  }, []);
  const onSelectDate = React.useCallback((date2) => {
    setDate(date2);
  }, []);
  const [getTimeSlots, timeSlots] = useTimeSlotsById({ timeFilters: activeTimeFilters });
  const { onPreferredTimeClick, onPreferredTimeFilterClick } = usePreferredTimeDataLayerEventDispatchers({
    appointmentCondition: "new",
    isVirtualAppointment,
    type,
    store,
    timeSlot
  });
  const calendarLoading = timeSlots.loading || bookAppointmentStore.loading || bookAppointmentType.loading || calendarAvailability.loading || storeOpeningTimes.loading || availabilityRequestParamsChanged;
  const nextAvailableDate = useNextAvailableDate(
    date,
    calendarAvailability,
    Boolean(!timeSlots.called || calendarLoading)
  );
  const nextWeekDate = useNextWeekDate(weeks, !date || Boolean(nextAvailableDate));
  const noTimeSlotsVariant = useNoTimeSlotsVariant(
    timeSlots,
    nextAvailableDate,
    date,
    closedDates,
    calendarLoading,
    isPreferredTimeFilterActive(active)
  );
  const onSelectTimeSlot = React.useCallback(
    (timeSlot2) => {
      setTimeSlot(timeSlot2);
      const { typeId, storeId, search, userLocation } = queryParams;
      goTo("who", {
        typeId,
        storeId,
        start: `${timeSlot2.from.getTime()}`,
        end: `${timeSlot2.until.getTime()}`,
        search,
        userLocation
      });
    },
    [setTimeSlot, goTo, queryParams]
  );
  const pushClickedOnAlternativeStoreEvent = React.useCallback(
    (store2) => {
      if (type && store2.globalStoreId) {
        const { id: typeId, ageGroup } = type;
        const dataLayerEvent = appointmentBookingAlternativeLocationEvent({
          condition_of_appointment: "new",
          ageGroup,
          appointment: {
            type_of_appointment: isVirtualAppointment ? "videocall" : "onsite",
            type_of_service: typeId
          },
          storeDetails: { id: store2.globalStoreId, city: store2.town, ...store2 },
          text_of_the_element: store2.name,
          // timeslots are always going to be undefined in this case
          time: {}
        });
        DataLayer.pushEvent(dataLayerEvent);
      }
    },
    [type, isVirtualAppointment]
  );
  const onSelectStore = React.useCallback(
    (store2, newDate) => {
      pushClickedOnAlternativeStoreEvent(store2);
      const invalidUrl = "##invalid_url##";
      const alternativeLocation = Util.getBookAppointmentUrl(invalidUrl, {
        paddedLength: appointmentBookingUrlPaddedLength,
        alternativeUrl: appointmentBookingAlternativeUrl && isMultilingualEnabled ? appointmentBookingAlternativeUrl[locale] : appointmentBookingAlternativeUrl,
        alternativeUrlStores: appointmentBookingAlternativeUrlStores
      })({ ...store2, globalStoreId: store2.globalStoreId ?? store2.code.toString() });
      if (!alternativeLocation.includes(invalidUrl)) {
        window.location.assign(alternativeLocation);
        return;
      }
      setStore(store2);
      const isNotTheSameDay = (d1, d2) => {
        const date1 = d1 ? DateUtils.toLocalDate(d1).split("T")[0] : void 0;
        const date2 = d2 ? DateUtils.toLocalDate(d2).split("T")[0] : void 0;
        return date1 !== date2;
      };
      if (newDate) {
        const newWeeks = getWeekDaysForWeeksFromDate(newDate);
        const newWeekDate = newWeeks[0]?.[0];
        if (isNotTheSameDay(newWeekDate, weekDate)) {
          setWeekDate(newWeekDate);
        }
        if (isNotTheSameDay(newWeeks[0]?.[0], weeks?.[0]?.[0])) {
          setWeeks(newWeeks);
        }
        if (isNotTheSameDay(date, newDate)) {
          setDate(newDate);
        }
      }
      const { typeId, search, userLocation } = queryParams;
      goTo("when", { typeId, storeId: store2.globalStoreId, search, userLocation });
    },
    [
      pushClickedOnAlternativeStoreEvent,
      appointmentBookingUrlPaddedLength,
      isMultilingualEnabled,
      locale,
      appointmentBookingAlternativeUrl,
      appointmentBookingAlternativeUrlStores,
      setStore,
      setDate,
      queryParams,
      goTo,
      weeks,
      date,
      weekDate
    ]
  );
  React.useEffect(() => {
    const { typeId, storeId } = queryParams;
    if (store && type && storeId === store.globalStoreId && typeId === type.id) {
      const dataLayerEvent = bookAppointmentWhenStepScreenEvent({
        store: !isVirtualAppointment ? {
          code: 0,
          globalStoreId: store.globalStoreId,
          streetName: store.streetName,
          town: store.town,
          name: store.name,
          postalCode: store.postalCode,
          streetNumber: store.streetNumber,
          additionalStreetInfo: store.additionalStreetInfo
        } : void 0,
        appointment: {
          appointmentType: {
            id: type.id,
            ageGroup: type.ageGroup,
            isVirtualAppointment
          }
        }
      });
      DataLayer.pushEvent(dataLayerEvent);
    }
  }, [store, type, queryParams, isVirtualAppointment]);
  const aabWhenPageView = React.useCallback(
    () => new Promise((resolve) => {
      if (!store || !store.globalStoreId || !type) {
        return;
      }
      resolve({
        id: "VirtualPage-View",
        Page_Type: "Info",
        Page_Section1: "Exam",
        Page_Section2: "Scheduler",
        Exam_Fitting: type.name,
        Store_Search_StoreId: store.globalStoreId
        // TODO: MACC-19077 Check if this is correct.
      });
    }),
    [store, type]
  );
  useTrackPageView(aabWhenPageView);
  const noTimeSlotsAction = React.useCallback(() => {
    switch (noTimeSlotsVariant) {
      case Variant.time:
      case Variant.date:
      case Variant.closedDate:
        if (!nextAvailableDate) {
          return;
        }
        setDate(nextAvailableDate);
        break;
      case Variant.week:
      case Variant.closedWeek:
        if (!nextWeekDate) {
          return;
        }
        setWeekDate(nextWeekDate);
        setDate(nextWeekDate);
        break;
      case Variant.error:
        const globalStoreId = store?.globalStoreId;
        const appointmentTypeId = type?.id ?? void 0;
        if (!date || !globalStoreId || appointmentTypeId === void 0) {
          return;
        }
        const dateString = DateUtils.toLocalDate(date);
        getTimeSlots(
          {
            variables: {
              globalStoreId,
              appointmentTypeId,
              date: dateString
            }
          },
          {
            timeSlotsReferTo: {
              date: dateString,
              store: globalStoreId
            }
          }
        );
      case Variant.none:
        return;
    }
  }, [noTimeSlotsVariant, nextAvailableDate, nextWeekDate, getTimeSlots, store, type, date]);
  const onToggleOtherTimeSlots = React.useCallback(() => {
    setShowOtherTimeSlots((curr) => !curr);
    setTimeout(() => {
      triggerScroll();
    }, 50);
  }, [triggerScroll]);
  React.useEffect(() => {
    const dateIsAlreadySelected = Boolean(date);
    const waitingForAvailability = !calendarAvailability.called || calendarAvailability.loading;
    if (dateIsAlreadySelected || !weeks || waitingForAvailability) {
      return;
    }
    const availableDate = firstAvailableDate(weeks, calendarAvailability.data);
    const fallbackDate = today.current;
    setDate(availableDate ?? fallbackDate);
  }, [date, weeks, calendarAvailability]);
  React.useEffect(() => {
    if (!store) {
      return;
    }
    getStoreOpeningTimes({
      variables: {
        globalStoreId: store.globalStoreId
      }
    });
  }, [getStoreOpeningTimes, store]);
  React.useEffect(() => {
    calendarAvailability.error && console.error("Error fetching appointment availability:", calendarAvailability.error.message);
  }, [calendarAvailability.error]);
  React.useEffect(() => {
    const globalStoreId = store?.globalStoreId;
    const appointmentTypeId = type?.id ?? void 0;
    if (!date || !globalStoreId || appointmentTypeId === void 0) {
      return;
    }
    const dateString = DateUtils.toLocalDate(date);
    getTimeSlots(
      {
        variables: {
          globalStoreId,
          appointmentTypeId,
          date: dateString
        }
      },
      {
        timeSlotsReferTo: {
          date: dateString,
          store: globalStoreId
        }
      }
    );
  }, [getTimeSlots, store, type, date]);
  React.useEffect(
    () => setShowOtherTimeSlots(
      timeSlots.filtered.included.length === 0 && timeSlots.filtered.excluded.length > 0
    ),
    [timeSlots.filtered]
  );
  const shouldRequestAlternativeStoreSuggestions = useShouldRequestAlternativeStoreSuggestions(
    timeSlots,
    date,
    store ?? void 0,
    timeSlots.metadata
  );
  const hasNoTimeSlotsAvailableInCalendar = getCalendarTimeSlotsAvailability(
    calendarAvailability.data,
    noTimeSlotsVariant
  );
  return /* @__PURE__ */ React.createElement(
    AppointmentDateTime,
    {
      ...rest,
      otherTimeSlotsElement,
      labels,
      onCalendarWeeksChange,
      store,
      appointmentType: type,
      selectedDate: date,
      onSelectDate,
      calendarLoading,
      calendarWeekDate: weekDate,
      calendarAvailability: calendarAvailability.data,
      calendarClosedDates: closedDates,
      calendarWeekCount: appointmentBookingAvailabiltyCalendarWeekCount,
      timeSlotsLoading: timeSlots.loading,
      timeSlots: timeSlots.filtered.included,
      onSelectTime: onSelectTimeSlot,
      noTimeSlotsVariant,
      noTimeSlotsAction,
      nextAvailableDate,
      noMoreAvailabilityUntilDate: nextWeekDate,
      shouldRequestAlternativeStoreSuggestions,
      showOtherTimeSlots,
      onToggleOtherTimeSlots,
      otherTimeSlots: timeSlots.filtered.excluded,
      onSelectStore,
      onPreferredTimeClick,
      onPreferredTimeFilterClick,
      isAabAlternativeStoreAvailabilityEnabled,
      hasNoTimeSlotsAvailableInCalendar,
      isVirtualappointment: isVirtualAppointment
    }
  );
};
export {
  AppointmentDateTimeContainer as default
};
//# sourceMappingURL=AppointmentDateTimeContainer.js.map
